import { useCallback, useEffect, useState } from 'react';
import { Alert, Loading, PageHeader } from '../App';
import { adminApi, dateTime, errorMessage, type PlatformSetting } from '../api/admin';

export function SettingsPage() {
  const [settings, setSettings] = useState<PlatformSetting[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      const list = await adminApi.settings();
      setSettings(list);
      setDrafts(Object.fromEntries(list.map((s) => [s.key, s.value])));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (setting: PlatformSetting) => {
    setSaving(setting.key);
    setError(null);
    setSuccess(null);

    try {
      await adminApi.updateSetting(setting.key, drafts[setting.key]);
      setSuccess(`${setting.key} saved.`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(null);
    }
  };

  const grouped = (settings ?? []).reduce<Record<string, PlatformSetting[]>>((groups, setting) => {
    (groups[setting.group] ??= []).push(setting);
    return groups;
  }, {});

  return (
    <>
      <PageHeader
        title="Platform settings"
        description="Values the platform reads at runtime. Changes take effect immediately, without a deployment."
      />

      <Alert message={error} />
      <Alert message={success} variant="success" />

      {!settings ? (
        <Loading rows={6} />
      ) : (
        Object.entries(grouped).map(([group, items]) => (
          <section className="mb-4" key={group}>
            <h2 className="h6 text-uppercase text-muted mb-2">{group}</h2>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th scope="col" style={{ width: '32%' }}>
                      Setting
                    </th>
                    <th scope="col" style={{ width: '22%' }}>
                      Value
                    </th>
                    <th scope="col">What it does</th>
                    <th scope="col" style={{ width: '12%' }} />
                  </tr>
                </thead>
                <tbody>
                  {items.map((setting) => {
                    const changed = drafts[setting.key] !== setting.value;

                    return (
                      <tr key={setting.key}>
                        <td>
                          <code className="small">{setting.key}</code>
                          {setting.isPublic && <div className="badge bg-secondary mt-1">Public</div>}
                        </td>

                        <td>
                          {setting.valueType === 'bool' ? (
                            <select
                              className="form-select form-select-sm"
                              value={drafts[setting.key]}
                              onChange={(e) => setDrafts({ ...drafts, [setting.key]: e.target.value })}
                              aria-label={setting.key}
                            >
                              <option value="true">true</option>
                              <option value="false">false</option>
                            </select>
                          ) : (
                            <input
                              className="form-control form-control-sm"
                              value={drafts[setting.key] ?? ''}
                              onChange={(e) => setDrafts({ ...drafts, [setting.key]: e.target.value })}
                              inputMode={setting.valueType === 'string' ? 'text' : 'decimal'}
                              aria-label={setting.key}
                            />
                          )}
                          <div className="form-text">{setting.valueType}</div>
                        </td>

                        <td className="small text-muted">
                          {setting.description}
                          {setting.updatedAtUtc && (
                            <div className="mt-1">Last changed {dateTime(setting.updatedAtUtc)}</div>
                          )}
                        </td>

                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={!changed || saving === setting.key}
                            onClick={() => save(setting)}
                          >
                            {saving === setting.key ? 'Saving…' : 'Save'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}

      <div className="admin-card">
        <h2 className="h6">Notes</h2>
        <ul className="small text-muted mb-0">
          <li>
            Changing the tax rate affects new purchases only. Subscriptions already sold keep the rate they were sold
            at, because the amount is snapshotted on the subscription.
          </li>
          <li>
            Turning off <code>Moderation.RequireListingApproval</code> means a paid listing goes live as soon as the
            payment is confirmed, with no human review.
          </li>
          <li>
            <code>Listing.AvailabilityFreshnessDays</code> controls how long an availability figure is trusted before
            customers see &ldquo;Availability unconfirmed&rdquo; instead of a number.
          </li>
        </ul>
      </div>
    </>
  );
}
